<template>
  <div>
    <DropDownMultiSelectSearch
      v-for="{ field, value } in filters"
      :key="field"
      :class="$style.dropdown"
      :errorText="errorText ? errorText[field] : ''"
      :loading="loading[field]"
      :multiselect="field !== eventFields.service"
      :name="field"
      :options="filterOptions[field]"
      :removable="!primaryFilters.includes(field)"
      :value="value"
      @input="v => handleFilterChange(field, v)"
      @remove="() => handleFilterRemove(field)"
    />
    <div :class="$style.addFilterContainer">
      <DropDownMultiSelectSearch
        v-show="availableFiltersToAdd.length"
        v-model="filtersToBeAdded"
        :class="$style.dropdown"
        :darkenWhenActive="true"
        :displayValue="false"
        :includeCaret="false"
        name="add filter"
        :options="availableFiltersToAdd"
        :useLinkStyleForButton="true"
        @close="handleFilterAdd"
      />
    </div>
    <a
      :class="[$style.clearAllLink, {
        [$style.clearAllLinkDisabled]: areFiltersCleared,
      }]"
      @click="handleClearAllClick"
    >
      clear all
    </a>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mixin as clickaway } from 'vue-clickaway';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import { intersection } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import ActivityLogFilterConsts from 'client/Insights/ActivityLog/constants/ActivityLogFilterConsts';
import DropDownMultiSelectSearch from 'components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';

const { ItemTypeConsts, StoreModuleMap } = Constants;
const { optionalFilters, primaryFilters } = ActivityLogFilterConsts;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemCollection = StoreModuleMap[ItemTypeConsts.system];

export default {
  name: 'ActivityLogFilters',

  components: {
    DropDownMultiSelectSearch,
  },

  mixins: [clickaway],

  props: {
    errorText: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    const loading = {};
    primaryFilters.forEach((filter) => {
      loading[filter] = false;
    });
    return {
      eventFields,
      filtersToBeAdded: [],
      loading,
      primaryFilters,
    };
  },

  computed: {
    ...mapGetters(insightsEventModule, ['areFiltersCleared']),
    ...mapState(insightsEventModule, [
      'filters',
      'filterOptions',
    ]),
    ...mapState(settingsModule, ['isCustomer']),

    availableFiltersToAdd() {
      // Find which filters haven't been added yet
      const filtersAdded = this.filters.map(d => d.field);
      return optionalFilters
        .filter(d => !filtersAdded.includes(d))
        .map(d => ({
          name: d,
          value: d,
        }))
        .sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
    },
  },

  EventHelper,

  methods: {
    ...mapActions(insightsEventModule, [
      'addFilterOption',
      'setFilterOptions',
      'addFilter',
      'clearFilters',
      'removeFilter',
      'setFilter',
      'setSelectedView',
    ]),
    ...mapActions(systemCollection, ['hasASystemOfOs']),

    handleClearAllClick() {
      if (!this.areFiltersCleared) {
        this.clearFilters();
        this.$emit('clearFilters');
      }
      this.setSelectedView(null);
    },

    handleFilterAdd() {
      this.filtersToBeAdded.forEach((filter) => {
        if (!this.filters.find(d => d.field === filter)) {
          this.$set(this.loading, filter, false);
          this.$emit('clearError', filter.field);
          this.addFilterOption(filter);
          this.addFilter(filter);
          this.$emit('add');
        }
      });
      this.filtersToBeAdded = [];
    },

    handleFilterChange(field, value) {
      // On change, ensure the value only includes options that are available for selection
      const optionValues = this.filterOptions[field].map(d => d.value);
      const newValue = intersection(value, optionValues);

      this.setFilter({ field, value: newValue });
      this.$emit('change', field, newValue);
      this.$options.EventHelper.triggerActivityLogFilterChanged({ field, filters: this.filters });
    },

    handleFilterRemove(field) {
      this.removeFilter(field);
      this.$emit('change');
    },
  },
};
</script>

<style module>
.dropdown {
  display: inline-block;
  margin-bottom: 5px;
  margin-right: 4px;
  position: relative;
}

a.clearAllLink {
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
  opacity: 0.9;
  user-select: none;
  white-space: nowrap;
}

a.clearAllLink:hover {
  opacity: 1;
}

a.clearAllLink:focus,
a.clearAllLink:hover {
  text-decoration: none;
}

a.clearAllLinkDisabled {
  color: var(--jcGrey400);
  cursor: default;
}

a.clearAllLinkDisabled:hover {
  color: var(--jcGrey400);
}

.addFilterContainer {
  display: inline-block;
  position: relative;
}

.kbArticleLink {
  color: var(--jcWhite50);
  text-decoration: underline;
}
</style>
